/**
 * Maps ClinicMetricSummary.metricKey → ClinicEvent.eventType so the metric
 * drawer can fetch the underlying raw events.
 *
 * IMPORTANT: keep in sync with apps/chiroup-backend-watchers/src/config/metricDefinitions.ts
 * Each entry here corresponds to a metricDefinition's eventTypes[0] value.
 *
 * Most metric keys are pluralized versions of event types
 * (e.g. "appointments_created" → "appointment_created"). The exceptions are:
 *   - "active_users" / "session_count" / "active_time_minutes" all derive from heartbeat
 *   - some keys are already singular (no transformation needed)
 */
const metricToEventTypeMap: Record<string, string> = {
  // Schedule
  appointments_created: 'appointment_created',
  appointments_checked_in: 'appointment_checked_in',
  appointments_checked_out: 'appointment_checked_out',
  clinic_schedule_updates: 'clinic_schedule_updated',
  scheduling_preferences_updates: 'scheduling_preferences_updated',
  online_scheduling_url_updates: 'online_scheduling_url_updated',
  online_bookings_user_toggles: 'online_bookings_user_toggled',
  online_booking_treatment_updates: 'online_booking_treatment_updated',

  // Clinical
  encounters_created: 'encounter_created',
  encounters_signed: 'encounter_signed',
  voice_to_chart_uses: 'voice_to_chart_used',
  pcp_initial_reports: 'pcp_initial_report_created',
  pcp_interim_reports: 'pcp_interim_report_created',
  pcp_release_reports: 'pcp_release_report_created',
  ccs_sent: 'ccs_sent',
  ccs_completed: 'ccs_completed',
  post_care_surveys_sent: 'post_care_survey_sent',
  post_care_surveys_completed: 'post_care_survey_completed',
  condition_references_accessed: 'condition_reference_accessed',
  condition_references_edited: 'condition_reference_edited',
  clinical_evaluations_accessed: 'clinical_evaluation_accessed',
  treatment_techniques_accessed: 'treatment_technique_accessed',
  research_reviews_accessed: 'research_review_accessed',

  // Billing
  purchases_created: 'purchase_created',
  claims_sent: 'claim_sent',
  claims_accepted: 'claim_accepted',
  payments_collected: 'payment_collected',
  eras_uploaded: 'era_uploaded',
  npi_rejections: 'npi_rejection',
  enrollment_rejections: 'enrollment_rejection',
  format_837_rejections: 'format_837_rejection',
  provider_loop_errors: 'provider_loop_error',
  referring_provider_errors: 'referring_provider_error',
  billing_info_added: 'billing_info_added',
  commercial_numbers_added: 'commercial_numbers_added',
  billing_global_settings_updates: 'billing_global_settings_updated',
  provider_billing_updates: 'provider_billing_updated',
  payors_added: 'payor_added',
  billing_codes_added: 'billing_code_added',
  no_show_cancel_fees_added: 'no_show_cancel_fee_added',
  no_show_cancel_fees_updated: 'no_show_cancel_fee_updated',
  packages_added: 'package_added',
  packages_updated: 'package_updated',

  // Patients
  patients_created: 'patient_created',
  condition_reports_sent: 'condition_report_sent',
  condition_reports_opened: 'condition_report_opened',
  google_review_links_sent: 'google_review_link_sent',
  healthcom_logins: 'healthcom_login',
  survey_preferences_updates: 'survey_preferences_updated',
  marketing_campaigns_accessed: 'marketing_campaign_accessed',

  // Setup
  clinic_logo_added: 'clinic_logo_added',
  facebook_linked: 'facebook_linked',
  google_review_url_added: 'google_review_url_added',
  clinic_locations_added: 'clinic_location_added',
  user_role_changes: 'user_role_changed',
  users_removed: 'user_removed_from_clinic',
  users_added: 'user_added_to_clinic',
  plan_changes: 'plan_changed',
  provider_schedule_updates: 'provider_schedule_updated',
  provider_signature_updates: 'provider_signature_updated',
  provider_profile_picture_updates: 'provider_profile_picture_updated',
  modalities_added: 'modality_added',
  supplements_added: 'supplement_added',
  healthcare_providers_added: 'healthcare_provider_added',
  tests_added: 'test_added',
  disciplines_added: 'discipline_added',
  disciplines_updated: 'discipline_updated',
  kiosk_connections: 'kiosk_connected',
  kiosk_disconnections: 'kiosk_disconnected',

  // Support
  features_suggested: 'feature_suggested',
  features_voted: 'feature_voted',
  knowledge_base_accessed: 'knowledge_base_accessed',
  forms_library_downloads: 'forms_library_downloaded',
};

export const metricKeyToEventType = (metricKey: string): string => {
  return metricToEventTypeMap[metricKey] || metricKey;
};
